import React, { useState, useEffect, useContext } from "react";
import { generateClient } from 'aws-amplify/api';
import { diff } from 'deep-object-diff';
import { MDBCard, MDBCardBody, MDBCardTitle, MDBCol, MDBIcon, MDBInput, MDBRow, MDBChip, MDBBtn, MDBSticky, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalBody, MDBModalFooter, MDBModalTitle, MDBSpinner, MDBFile, MDBTooltip } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import CountrySelect from "../../components/CountrySelect";
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { convertHTMlToRaw } from "../../utils/HTMLDraft";

import { fullCompanyQuery, fullCompanyMutation } from "../../graphql-custom/companyQueryies";
import { captureError } from '../../utils/sentryHelpers';
import { useIndustry } from '../../utils/industryContextHook';
import ErrorFallback from '../../components/toolbox/ErrorFallback';
import SlugField from '../../components/toolbox/SlugField';
import { isAdminOrModerator } from '../../utils/roles';
import { slugMutationInput } from '../../utils/slugInput';

import SelectTopics from "../../components-user/topics/SelectTopics";
import ReactCountryFlag from "react-country-flag";
import { UserContext } from "../../App";

const client = generateClient();
const StateProofImage = React.memo(function Image({ src, alt, className, style }) {
  return <img src={src} alt={alt} className={className} style={style} />;
});

const deepUpdateObject = (object, path, value) => {
  const pathParts = path.split('.');
  let newObject = { ...object };

  pathParts.reduce((o, p, i) => {
    if (i === pathParts.length - 1) {
      o[p] = value; // Setzt den Wert am Ende des Pfades
    } else {
      o[p] = o[p] ? { ...o[p] } : {}; // Erstellt eine Kopie des nächsten Objekts im Pfad, falls es existiert, sonst ein neues Objekt
    }
    return o[p];
  }, newObject);

  return newObject;
};


export default function EditCompany_General({ presetCompanyId, toggleSaveChanges, handleChangeComplete }) {
  const { t, i18n } = useTranslation();
  const { user } = useContext(UserContext);
  const { currentIndustry } = useIndustry();
  const companyid = presetCompanyId || (window.location.pathname.includes("mycompany") ? window.location.pathname.split("/")[2] : window.location.pathname.split("/")[3]);

  // Company data
  const [initialCompany, setInitialCompany] = useState({});
  const [company, setCompany] = useState({});
  const [changes, setChanges] = useState({});

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Get data
  const getCompany = async () => {
    try {
      const company = await client.graphql({
        variables: { id: companyid },
        query: fullCompanyQuery
      });
      setSelectedTopics(company.data.getCompany.topics.items.map((topic) => topic.topic.id));
      return {
        ...company.data.getCompany,
        topics: company.data.getCompany.topics.items.map((topic) => topic.topic.id)
      };
    } catch (error) {
      captureError(error, {
        feature: 'company-management',
        operation: 'fetch-company-profile',
        context: { companyId: companyid },
        tags: { component: 'Profile' }
      });
    }
  }

  const intialLoadCompany = async () => {
    setLoading(true);
    const companyData = await getCompany();
    if (!companyData) {
      setLoadError(true);
      setLoading(false);
      return;
    }
    setInitialCompany(companyData);
    setCompany(companyData);
    if (companyData.description) {
      const deContent = companyData.description.find(c => c.language === "de")?.text;
      const enContent = companyData.description.find(c => c.language === "en")?.text;
      setEditorState({
        de: deContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(deContent)
          )
        ) : EditorState.createEmpty(),
        en: enContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(enContent)
          )
        ) : EditorState.createEmpty(),
      });
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!companyid) {
      return;
    };
    intialLoadCompany();
  }, []);

  // Changes and form handling

  const handleChange = (e) => {
    // Get values from input
    const datapath = e.target.attributes.datapath.value;
    const value = e.target.value;

    // Update current company object
    const newCompany = deepUpdateObject(company, datapath, value)
    setCompany(newCompany);
    setChanges(diff(initialCompany, newCompany));
  }


  const extractUpdatedTopLevelObjects = (company, changes) => {
    const updatedObjects = {};

    Object.keys(changes).forEach(topLevelKey => {
      if (company.hasOwnProperty(topLevelKey)) {
        updatedObjects[topLevelKey] = company[topLevelKey];
      }
    });

    return updatedObjects;
  };
  // Update company
  // Website must start with http:// or https:// for AWSURL validation
  const isWebsiteInvalid = company.website && !/^https?:\/\//i.test(company.website);

  const [isUpdating, setIsUpdating] = useState(false);
  const updateCompany = async () => {
    if (Object.keys(changes).length === 0) return;
    if (isWebsiteInvalid) return;
    try {
      let updateObject = extractUpdatedTopLevelObjects(company, changes);
      const topics = updateObject.topics;
      delete updateObject.topics;
      // slug (Admin/Moderator only, in the diff only when edited here): normalized and only sent when it
      // differs from the loaded slug, null when a previously set slug was cleared (see slugMutationInput)
      if ('slug' in updateObject) {
        delete updateObject.slug;
        Object.assign(updateObject, slugMutationInput(company.slug, { canEditSlug, isUpdate: true, loadedSlug: initialCompany.slug }));
      }
      if (Object.keys("updateObject").length > 1 || (changes.length === 1 && !changes.hasOwnProperty("topics"))) {
        const updatedCompany = await client.graphql({
          variables: { input: { ...updateObject, id: companyid } },
          query: fullCompanyMutation
        });
        setInitialCompany(updatedCompany.data.updateCompany);
        setCompany(updatedCompany.data.updateCompany);
      }
      async function fetchExistingTopics(companyId, client) {
        const query = /* GraphQL */ `
                  query ListCompaniesTopics(
                      $filter: ModelCompaniesTopicsFilterInput
                      $limit: Int
                      $nextToken: String
                  ) {
                      listCompaniesTopics(
                          filter: $filter
                          limit: $limit
                          nextToken: $nextToken
                      ) {
                          items {
                              id
                              topicId
                          }
                      }
                  }
              `;

        const response = await client.graphql({
          query,
          variables: { filter: { companyId: { eq: companyId } }, limit: 10000 }
        });

        return response.data.listCompaniesTopics.items || [];
      }

      async function addTopic(topic, client) {
        const mutation = /* GraphQL */ `
                  mutation CreateCompaniesTopics(
                      $input: CreateCompaniesTopicsInput!
                      $condition: ModelCompaniesTopicsConditionInput
                  ) {
                      createCompaniesTopics(input: $input, condition: $condition) {
                          id
                      }
                  }
              `;
        await client.graphql({
          query: mutation,
          variables: { input: { companyId: topic.companyId, topicId: topic.topicId } }
        });
      }

      async function removeTopic(companyTopicId, client) {
        const mutation = /* GraphQL */ `
                  mutation DeleteCompaniesTopics(
                      $input: DeleteCompaniesTopicsInput!
                      $condition: ModelCompaniesTopicsConditionInput
                  ) {
                      deleteCompaniesTopics(input: $input, condition: $condition) {
                          id
                      }
                  }
              `;

        await client.graphql({
          query: mutation,
          variables: { input: { id: companyTopicId } }
        });
      }
      const existingTopicConnections = await fetchExistingTopics(companyid, client);
      const existingTopicIds = existingTopicConnections.map(t => t.topicId);

      const topicsToAdd = selectedTopics.filter(id => !existingTopicIds.includes(id));
      const topicsToRemove = existingTopicConnections.filter(t => !selectedTopics.includes(t.topicId));

      // Füge neue Topics hinzu
      await Promise.all(topicsToAdd.map(async topicId => {
        await addTopic({ topicId: topicId, companyId: companyid }, client);
      }));

      // Entferne überflüssige Topics
      await Promise.all(topicsToRemove.map(async topicConnection => {
        await removeTopic(topicConnection.id, client);
      }));

      setChanges({});

    } catch (error) {
      captureError(error, {
        feature: 'company-management',
        operation: 'update-company-profile',
        context: { companyId: companyid, changes },
        tags: { component: 'Profile' }
      });
    }
  }

  const handleUpdateCompany = async () => {
    setIsUpdating(true);
    await updateCompany();
    setIsUpdating(false);
    if (toggleSaveChanges) handleChangeComplete("companyProfile");
  }

  const handleResetChanges = () => {
    setCompany(initialCompany);
    setChanges({});
  }

  // Check if user has Admin cognito group
  const isAdmin = user?.["cognito:groups"]?.includes("Admin");
  // Public URL slug: only Admin/Moderator may see and write it (AppSync field auth rejects
  // any other mutation that contains "slug"). The diff-based update below only sends it
  // when it was edited here, so CompanyAdmin inputs never contain the key.
  const canEditSlug = isAdminOrModerator(user);
  const [nameFieldLocked, setNameFieldLocked] = useState(!isAdmin);
  const [showNameLockModal, setShowNameLockModal] = useState(false);
  const toggleNameLockModal = () => setShowNameLockModal(!showNameLockModal);

  // Editor
  const [editorState, setEditorState] = useState({
    de: EditorState.createEmpty(),
    en: EditorState.createEmpty(),
  })
  const [editorModes, setEditorModes] = useState({ showDraft: true, showHTML: false })
  const onEditorStateChange = (editorState, language) => {
    setEditorState(oldState => ({ ...oldState, [language]: editorState }));
    setCompany(oldCompany => ({ ...oldCompany, description: [...oldCompany.description?.filter(c => c.language !== language) ?? [], { language, text: convertHTMlToRaw(editorState) }] }));
    setChanges(diff(initialCompany, company));
  }
  const ChangeHTMLDraft = () => {
    if (editorModes.showDraft && !editorModes.showHTML) {
      setEditorModes({ showDraft: false, showHTML: true })
    } else if (!editorModes.showDraft && editorModes.showHTML) {
      setEditorModes({ showDraft: true, showHTML: false })
    }
  }
  const ChangeDraftHTMLBtn = () => <div className="rdw-option-wrapper" onClick={ChangeHTMLDraft}><MDBIcon icon="code" /></div>

  // Select Topics 
  const [topics, setTopics] = useState([]);
  const [topicGroups, setTopicGroups] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);


  const [selectTopicsModal, setSelectTopicsModal] = useState(false);
  const toggleSelectTopicsModal = () => setSelectTopicsModal(!selectTopicsModal);


  const handleSetSelectedTopics = (topics) => {
    setSelectedTopics(topics);
    handleChange({
      target: {
        value: topics,
        attributes: {
          datapath: {
            value: "topics"
          }
        },
      }
    })
  }

  // Topics data

  const getTopics = async () => {
    const filter = currentIndustry?.id ? { industryId: { eq: currentIndustry.id } } : undefined;
    try {
      const topicGroupsWithTopics = await client.graphql({
        query: /* GraphQL */ `
            query ListTopicGroups(
              $filter: ModelTopicGroupFilterInput
              $limit: Int
              $nextToken: String
            ) {
              listTopicGroups(filter: $filter, limit: $limit, nextToken: $nextToken) {
                items {
                  id
                  color
                  sortKey
                  name {
                    language
                    text
                  }
                  topics {
                    items {
                      id
                      name {
                        language
                        text
                      }
                      topicGroupTopicsId
                    }
                  }
                }
                nextToken
                __typename
              }
            }
          `,
        variables: {
          filter,
          limit: 1000
        }
      });
      setTopicGroups(topicGroupsWithTopics.data.listTopicGroups.items.sort((a, b) => a.sortKey - b.sortKey));
      const topics = topicGroupsWithTopics.data.listTopicGroups.items.map((topicGroup) => topicGroup.topics.items).flat();
      setTopics(topics);

    } catch (error) {
      captureError(error, {
        feature: 'company-management',
        operation: 'fetch-topics',
        tags: { component: 'Profile' }
      });
    }
  }

  useEffect(() => {
    if (currentIndustry?.id) {
      getTopics();
    }
  }, [currentIndustry?.id]);

  // Filter selectedTopics to only show topics from current industry
  // IMPORTANT: Always filter from company.topics (original data), not from selectedTopics
  useEffect(() => {
    if (topics.length > 0 && company.topics?.length > 0) {
      const validTopicIds = topics.map(t => t.id);
      const filteredTopics = company.topics.filter(topicId => validTopicIds.includes(topicId));

      // Only update if there's a difference to avoid infinite loops
      const currentSorted = [...selectedTopics].sort().join(',');
      const filteredSorted = [...filteredTopics].sort().join(',');

      if (currentSorted !== filteredSorted) {
        setSelectedTopics(filteredTopics);
      }
    }
  }, [topics, currentIndustry?.id, company.topics]); // Re-run when topics, industry, or company data changes

  const Topics = ({ topics, topicGroups, selectedTopics }) => {

    // First sort the topics by topicGroup (new array with objects for eacht group, inside object the key: topics, inside the topics for this group), then return for each group a col, inside the col the title of the group, then the topics as chips
    if (!topics || topics?.length === 0 || !topicGroups || topicGroups?.length === 0 || selectedTopics?.length === 0) {
      return null;
    }
    let sortedTopics = [];
    selectedTopics.forEach((selectedTopic) => {
      const topicData = topics.find((topic) => topic.id === selectedTopic);

      // Skip if topic doesn't exist in current industry
      if (!topicData) return;

      const topicGroup = topicGroups.find((topicGroup) => topicGroup.id === topicData.topicGroupTopicsId);

      // Skip if topic group doesn't exist (defensive programming)
      if (!topicGroup) return;

      const topicGroupIndex = sortedTopics.findIndex((sortedTopic) => sortedTopic.id === topicGroup.id);
      if (topicGroupIndex === -1) {
        sortedTopics.push({ ...topicGroup, topics: [topicData] });
      } else {
        sortedTopics[topicGroupIndex].topics.push(topicData);
      }
    });
    return (
      <MDBRow className="my-3 my-lg-4">
        {sortedTopics.map((topicGroup) => {
          return (
            <MDBCol size="12" xxl="6" key={topicGroup.id} className="mb-3 mb-lg-4">
              <h6>{topicGroup.name?.find(n => n.language === i18n.language)?.text || topicGroup.name?.[0]?.text}</h6>
              <MDBRow>
                {topicGroup.topics.map((topic) => {
                  return (
                    <MDBCol size="12" key={topic.id}>
                      <MDBChip className="m-1 float-start">{topic?.name?.find((name) => name.language === i18n.language)?.text || topic.name[0]?.text}</MDBChip>
                    </MDBCol>
                  )
                })}
              </MDBRow>
            </MDBCol>
          )
        })}
      </MDBRow>
    )
  }

  // User Image
  const [isUploadingImageStates, setIsUploadingImageStates] = useState({});
  const [uploadingImageErrors, setUploadingImageErrors] = useState({});

  const handleSetImage = (file, type) => {
    // upload file using mutation: uploadFile(base64FileBuffer: String, companyId: String): LambdaS3Response @function(name: "uploadFiles-${env}")
    if (file) {
      try {
        setIsUploadingImageStates(prevState => ({ ...prevState, [type]: true }));
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = () => {
          setIsUploadingImageStates(prevState => ({ ...prevState, [type]: false }));
        }
        reader.onload = async () => {
          try {
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: /* GraphQL */ `
              mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
                uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
              }
            `,
              variables: {
                imageBuffer: base64FileBuffer,
                imageType: type === "image" ? "companyLogo" : "companyBanner",
              },
            });
            const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
            handleChange({
              target: {
                value: {
                  fileName,
                  alt: " Image of " + company.name,
                },
                attributes: {
                  datapath: {
                    value: type === "image" ? "image.main" : "banner",
                  }
                },
              },
            });
          } catch (error) {
            captureError(error, {
              feature: 'company-management',
              operation: 'upload-company-image-inner',
              context: { companyId: companyid, imageType: type },
              tags: { component: 'Profile' }
            });
            setUploadingImageErrors(prevState => ({ ...prevState, [type]: error.errors[0].message }));
            setIsUploadingImageStates(prevState => ({ ...prevState, [type]: false }));
          }
        }
        setIsUploadingImageStates(prevState => ({ ...prevState, [type]: false }));
        setUploadingImageErrors(prevState => ({ ...prevState, [type]: null }));
      } catch (error) {
        captureError(error, {
          feature: 'company-management',
          operation: 'upload-company-image',
          context: { companyId: companyid, imageType: type },
          tags: { component: 'Profile' }
        });
        setUploadingImageErrors(prevState => ({ ...prevState, [type]: error.errors[0].message }));
        setIsUploadingImageStates(prevState => ({ ...prevState, [type]: false }));
      }
    }
  }

  useEffect(() => {
    if (toggleSaveChanges) {
      handleUpdateCompany();
    }
  }, [toggleSaveChanges])

  if (loadError) return <ErrorFallback />;

  return (
    <>
      {
        typeof toggleSaveChanges !== "boolean" &&
        <MDBSticky
          offset={60}
          boundary
          tag='div'
          href=''

        >
          <MDBRow className="mb-3" >
            <MDBCol size="12" className="text-end">
              <div style={{ backgroundColor: "white", width: "fit-content", float: "right" }}>
                <MDBBtn onClick={() => handleResetChanges()} color='light' rippleColor='dark' size="sm" >
                  {t("Reset Changes")}
                </MDBBtn>
                <MDBBtn onClick={() => handleUpdateCompany()} color="success" outline>
                  {t("Save")}
                </MDBBtn>
              </div>

            </MDBCol>
          </MDBRow>
        </MDBSticky>
      }

      <MDBRow className="mb-2 mb-xl-4">
        <MDBCol size="12" lg="4">
          <div className="text-center">
            <div className="w-100 mb-1">
              <StateProofImage alt={company.image?.main?.alt || "Company Image"}
                src={company.image?.main?.fileName ? `https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${company.image.main.fileName}` : "//cdn.eu.com/connect/img/image-placeholder.jpg"}
                className="img-thumbnail border"
                style={{ height: "200px", width: "200px", objectFit: "contain" }}
              />
            </div>
            <span className="text-muted" style={{ fontSize: "smaller" }}>{t("Recommended format: Square, min 1000px * 1000px")}</span><br />
            <MDBBtn color="primary" onClick={() => document.getElementById("imageMain").click()}>{t("Upload Logo")}</MDBBtn>
            <MDBFile className="d-none" size='sm' id='imageMain' onChange={e => handleSetImage(e.target.files[0], "image")} />
            {
              isUploadingImageStates.image && <span><MDBSpinner role="status" /></span>
            }
            {
              uploadingImageErrors.image && <span className="mt-3 text-danger fw-bold">
                {uploadingImageErrors.image}
              </span>
            }
          </div>
        </MDBCol>
        <MDBCol size="12" lg="8">
          <div className="text-center">
            <div className="w-100 mb-1">
              <StateProofImage alt={company.banner?.alt || "Company Image"}
                src={company.banner?.fileName ? `https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${company.banner.fileName}` : "//cdn.eu.com/connect/img/image-placeholder.jpg"}
                className="border bg-white"
                style={{ height: "200px", maxWidth: "100%", width: "auto", aspectRatio: "16 / 5", objectFit: "cover" }}
              />
            </div>
            <span className="text-muted" style={{ fontSize: "smaller" }}>{t("Recommended format: 16:5, e.g. 1600px * 500px")}</span><br />
            <MDBBtn color="primary" onClick={() => document.getElementById("imageBanner").click()}>{t("Upload Banner")}</MDBBtn>
            <MDBFile className="d-none" size='sm' id='imageBanner' onChange={e => handleSetImage(e.target.files[0], "banner")} />
            {
              isUploadingImageStates.banner && <span><MDBSpinner role="status" /></span>
            }
            {
              uploadingImageErrors.banner && <span className="mt-3 text-danger fw-bold">
                {uploadingImageErrors.banner}
              </span>
            }
          </div>


        </MDBCol>
      </MDBRow>

      <MDBCard className="mb-4">
        <MDBCardBody>
          <MDBCardTitle className="mb-4">{t("Informations")}</MDBCardTitle>
          {loading || !company ?
            <div>{t("Loading...")}</div>
            :
            <>
              <MDBRow>
                <MDBCol md="6">
                  <h5>{t("Address")}</h5>
                  <h6>{t("Name")}</h6>
                  <MDBRow className="mb-3">
                    <MDBCol size="11">
                      <MDBInput disabled={nameFieldLocked} autoComplete="off" datapath="name" label={t("Name")} value={company.name || ""} onChange={handleChange} />
                    </MDBCol>
                    <MDBCol size="1" className="text-center pt-2">
                      {nameFieldLocked ?
                        <MDBIcon fas icon="unlock-alt" onClick={() => isAdmin ? setNameFieldLocked(false) : toggleNameLockModal()} style={{ cursor: 'pointer' }} />
                        :
                        <MDBIcon fas icon="lock" onClick={() => setNameFieldLocked(!nameFieldLocked)} style={{ cursor: 'pointer' }} />
                      }
                    </MDBCol>
                  </MDBRow>
                  {canEditSlug && (
                    <MDBRow className="mb-3">
                      <MDBCol size="11">
                        <SlugField
                          entity="company"
                          entityId={companyid}
                          value={company.slug}
                          history={company.slugHistory}
                          sourceText={company.name}
                          disabled={isUpdating}
                          onChange={(slug) => handleChange({ target: { value: slug || null, attributes: { datapath: { value: "slug" } } } })}
                        />
                      </MDBCol>
                    </MDBRow>
                  )}
                  <h6>{t("Address")}</h6>
                  <MDBRow className="mb-3">
                    <MDBCol size="8">
                      <MDBInput autoComplete="off" datapath="contact.address.street" label={t("Street")} value={company.contact?.address?.street || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                    <MDBCol size="4">
                      <MDBInput autoComplete="off" datapath="contact.address.streetNumber" label={t("Number")} value={company.contact?.address?.streetNumber || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                  </MDBRow>
                  <MDBRow className="mb-3">
                    <MDBCol>
                      <MDBInput autoComplete="off" datapath="contact.address.street2" label={t("Street Additonal")} value={company.contact?.address?.street2 || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                  </MDBRow>
                  <MDBRow className="mb-3">

                    <MDBCol>
                      <MDBInput autoComplete="off" datapath="contact.address.zip" label={t("Zip")} value={company.contact?.address?.zip || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                    <MDBCol>
                      <MDBInput autoComplete="off" datapath="contact.address.city" label={t("City")} value={company.contact?.address?.city || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                  </MDBRow>
                  <MDBRow className="mb-3">
                    <MDBCol>
                      <CountrySelect datapath="contact.address.countryCode" value={company.contact?.address?.countryCode || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                  </MDBRow>
                  <h6>{t("Contact")}</h6>
                  <MDBRow className="mb-3">
                    <MDBCol size="12" md="4" className="mb-3 mb-md-0">
                      <MDBInput
                        autoComplete="off"
                        value={company.contact?.phone || ""}
                        label={t("Phone")}
                        datapath="contact.phone"
                        onChange={handleChange}
                        disabled={isUpdating}
                      />

                    </MDBCol>
                    <MDBCol size="12" md="4" className="mb-3 mb-md-0">
                      <MDBInput
                        autoComplete="off"
                        value={company.contact?.mobile || ""}
                        label={t("Mobile")}
                        datapath="contact.mobile"
                        onChange={handleChange}
                        disabled={isUpdating}
                      />
                    </MDBCol>
                    <MDBCol size="12" md="4">
                      <MDBInput
                        autoComplete="off"
                        value={company.contact?.fax || ""}
                        label={t("Fax")}
                        datapath="contact.fax"
                        onChange={handleChange}
                        disabled={isUpdating}
                      />
                    </MDBCol>
                  </MDBRow>
                  <MDBRow className="mb-0"/* last block does not need margin */>
                    <MDBCol size="12" md="6" className="mb-3 mb-md-0">
                      <MDBInput autoComplete="off" datapath="contact.email" label={t("Email")} value={company.contact?.email || ""} onChange={handleChange} disabled={isUpdating} />
                    </MDBCol>
                    <MDBCol size="12" md="6">
                      <MDBTooltip tag='span' title={isWebsiteInvalid ? t("URL must start with https:// or http://") : ''} disableHoverListener={!isWebsiteInvalid}>
                        <MDBInput autoComplete="off" datapath="website" label={t("Website")} value={company.website ?? ""} onChange={handleChange} disabled={isUpdating} className={isWebsiteInvalid ? 'is-invalid' : ''} style={isWebsiteInvalid ? { marginBottom: 0 } : undefined} />
                      </MDBTooltip>
                    </MDBCol>
                  </MDBRow>
                </MDBCol>
                <hr className="w-100 d-md-none" />
                <MDBCol md="6" className="text-center">
                  <MDBBtn onClick={() => toggleSelectTopicsModal()} color="primary" outline>
                    {t("Select topics")}
                  </MDBBtn>
                  {/* <h5>{t("Selected Topics")}</h5> */}

                  <MDBRow className="w-100 mx-0">
                    <MDBCol>
                      <Topics
                        topics={topics}
                        topicGroups={topicGroups}
                        selectedTopics={selectedTopics}


                      />
                    </MDBCol>
                  </MDBRow>
                </MDBCol>



              </MDBRow>
            </>
          }
        </MDBCardBody>
      </MDBCard>

      <MDBCard>
        <MDBCardBody>
          <MDBCardTitle className="mb-4">{t("Description")}</MDBCardTitle>
          {
            editorState &&
            <>
              <ReactCountryFlag
                countryCode="de"
                svg
                style={{
                  width: "20px",
                  height: "20px",
                }}
                title={i18n.language}
              />
              <Editor
                editorState={editorState["de"]}
                onEditorStateChange={e => onEditorStateChange(e, "de")}
                editorClassName={editorModes.showDraft ? "md-textarea form-control" : "md-textarea form-control d-none"}
                toolbarClassName="toolbarClassName"
                wrapperClassName="wrapperClassName"
                toolbarCustomButtons={[<ChangeDraftHTMLBtn />]}
                toolbar={{
                  options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                }}
              />
              <br></br>
              <ReactCountryFlag
                countryCode="gb"
                svg
                style={{
                  width: "20px",
                  height: "20px",
                }}
                title={i18n.language}
              />
              <Editor
                editorState={editorState["en"]}
                onEditorStateChange={e => onEditorStateChange(e, "en")}
                editorClassName={editorModes.showDraft ? "md-textarea form-control" : "md-textarea form-control d-none"}
                toolbarClassName="toolbarClassName"
                wrapperClassName="wrapperClassName"
                toolbarCustomButtons={[<ChangeDraftHTMLBtn />]}
                toolbar={{
                  options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                }}
              />
            </>
          }

        </MDBCardBody>
      </MDBCard>
      <MDBModal open={selectTopicsModal} onClose={() => setSelectTopicsModal(false)}>
        <MDBModalDialog size="xl" scrollable>
          <MDBModalContent >
            <MDBModalHeader>
              <MDBModalTitle>{t("Select Topics")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleSelectTopicsModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <SelectTopics
                isActive={selectTopicsModal}
                toggleActive={toggleSelectTopicsModal}
                selectedTopics={company.topics || []}
                setSelectedTopics={handleSetSelectedTopics}
                parentTopics={topics}
                parentTopicGroups={topicGroups}
                maxSelect={5}
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={toggleSelectTopicsModal}>{t("Close")}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MDBModal open={showNameLockModal} onClose={() => setShowNameLockModal(false)}>
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Company Name")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleNameLockModal}></MDBBtn>
            </MDBModalHeader>
            <MDBModalBody>
              <p>{t("To change the company name, please contact our support team.")}</p>
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="primary" onClick={toggleNameLockModal}>{t("Close")}</MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    </>
  )
}
